import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw,
  FileSpreadsheet,
  Download,
  ExternalLink,
  Check,
  Loader2,
  Search,
  FileWarning,
} from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const fmt = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-border p-3 min-w-[120px]">
      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className={`font-heading text-2xl mt-1 ${tone || ""}`}>{value}</p>
    </div>
  );
}

export default function InvoiceReconciliation() {
  const [data, setData] = useState({ items: [], pending: 0, received: 0, sheet_url: "", last_synced: "" });
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoice-recon");
      setData(data);
      if (data.sheet_url) setUrl(data.sheet_url);
    } catch (e) {
      toast.error("Could not load invoice reconciliation");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    if (!url.trim()) return toast.error("Paste the Google Sheet link first");
    setSyncing(true);
    try {
      const { data } = await api.post("/invoice-recon/sync", { sheet_url: url.trim() });
      toast.success(`Synced — ${data.pending} pending, ${data.received} with invoice`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const markReceived = async (it, val) => {
    try {
      await api.patch(`/invoice-recon/${it.id}`, { manual_received: val });
      setData((d) => {
        let pending = d.pending;
        let received = d.received;
        if (it.is_pending) {
          if (val) {
            pending -= 1;
            received += 1;
          } else {
            pending += 1;
            received -= 1;
          }
        }
        return {
          ...d,
          pending,
          received,
          items: d.items.map((x) =>
            x.id === it.id
              ? { ...x, manual_received: val, effective_status: val ? "received" : x.is_pending ? "pending" : "received" }
              : x
          ),
        };
      });
    } catch {
      toast.error("Could not update");
    }
  };

  const saveNote = async (it, note) => {
    try {
      await api.patch(`/invoice-recon/${it.id}`, { note });
    } catch {
      toast.error("Could not save note");
    }
  };

  const downloadTemplate = () => {
    const u = `${API}/invoice-recon/gstr2a-template?month=${month}`;
    fetch(u, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `GSTR2A-template-${month}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(`GSTR-2A template for ${month} downloaded`);
      })
      .catch(() => toast.error("Could not generate the template"));
  };

  const filtered = useMemo(() => {
    const items = data.items || [];
    const byTab = items.filter((x) =>
      tab === "all" ? true : (x.effective_status || (x.is_pending ? "pending" : "received")) === tab
    );
    if (!q.trim()) return byTab;
    const s = q.toLowerCase();
    return byTab.filter((x) =>
      `${x.vendor_guess} ${x.narration} ${x.vch_no} ${x.amount}`.toLowerCase().includes(s)
    );
  }, [data, tab, q]);

  return (
    <div className="space-y-6" data-testid="invoice-recon-page">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-brand" /> Invoice Reconciliation
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Pulls the pending purchase invoices from your shared bank sheet (payments with no invoice
          attached yet) so the team can chase them and file GSTR-2A. Already-uploaded invoices are
          flagged <span className="text-brand">in app</span>.
        </p>
      </div>

      <Card className="p-4 space-y-3" data-testid="invoice-recon-sync-bar">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Paste the Google Sheet link (Anyone with link can view)…"
              className="h-9"
              data-testid="sheet-url-input"
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data.sheet_url && (
              <a href={data.sheet_url} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="open-sheet-btn">
                  <ExternalLink className="h-3.5 w-3.5" /> Open sheet
                </Button>
              </a>
            )}
            <Button onClick={sync} disabled={syncing} className="gap-2" data-testid="sync-sheet-btn">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync now
            </Button>
          </div>
        </div>
        {data.last_synced && (
          <p className="text-[11px] text-muted-foreground">Last synced {new Date(data.last_synced).toLocaleString()}</p>
        )}
      </Card>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex gap-3">
          <Stat label="Pending invoices" value={data.pending} tone="text-amber-400" />
          <Stat label="With invoice" value={data.received} tone="text-emerald-400" />
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">GSTR-2A month</label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-9 w-40 mt-1"
              data-testid="gstr2a-month"
            />
          </div>
          <Button onClick={downloadTemplate} variant="outline" className="gap-2" data-testid="download-gstr2a-btn">
            <Download className="h-4 w-4" /> GSTR-2A template
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1" role="tablist" data-testid="invoice-recon-tabs">
          {[["pending", "Pending"], ["received", "With invoice"], ["all", "All"]].map(([k, l]) => (
            <button
              key={k}
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                tab === k ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`invoice-recon-tab-${k}`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search vendor / vch / amount"
            className="h-9 pl-8 w-64"
            data-testid="invoice-recon-search"
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-brand" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <FileWarning className="h-8 w-8 opacity-40" />
            {data.items.length === 0 ? "Paste your sheet link and hit Sync now." : "Nothing here."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Vch</th>
                  <th className="px-3 py-2">Vendor / narration</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Note</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((it) => {
                  const received = it.effective_status === "received" || it.manual_received || !it.is_pending;
                  return (
                    <tr key={it.id} className="hover:bg-white/[0.02]" data-testid={`invoice-recon-row-${it.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{it.date || it.date_raw || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{it.vch_no}</td>
                      <td className="px-3 py-2 max-w-[360px]">
                        <p className="truncate">{it.vendor_guess || it.narration}</p>
                        {it.narration && it.narration !== it.vendor_guess && (
                          <p className="text-[11px] text-muted-foreground truncate">{it.narration}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(it.amount)}</td>
                      <td className="px-3 py-2">
                        {received ? (
                          it.has_link ? (
                            <a href={it.drive_link} target="_blank" rel="noreferrer">
                              <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/30 gap-1">
                                <ExternalLink className="h-3 w-3" /> Invoice
                              </Badge>
                            </a>
                          ) : (
                            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/30">Received</Badge>
                          )
                        ) : it.in_app ? (
                          <Badge className="bg-brand/15 text-brand border-brand/30">In app · not shared</Badge>
                        ) : (
                          <Badge className="bg-amber-500/15 text-amber-300 border-amber-400/30">
                            {it.sub_status || "Pending"}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          defaultValue={it.note || ""}
                          onBlur={(e) => e.target.value !== (it.note || "") && saveNote(it, e.target.value)}
                          placeholder="add note…"
                          className="h-8 w-40"
                          data-testid={`invoice-recon-note-${it.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        {(it.is_pending || it.manual_received) && (
                          <Button
                            size="sm"
                            variant={received ? "ghost" : "outline"}
                            onClick={() => markReceived(it, !it.manual_received)}
                            className="gap-1.5"
                            data-testid={`invoice-recon-mark-${it.id}`}
                          >
                            <Check className="h-3.5 w-3.5" />
                            {it.manual_received ? "Undo" : "Mark received"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
